<template>
    <IonPage
        :data-mode="dataMode"
        style="--ion-background-color: var(--surface-primary);"
    >
        <IonHeader
            class="ion-no-border"
            :class="{
                'surface-primary': !headerImage,
            }"
        >
            <!-- Add the header over the content in the fixed slot -->
            <AppHeader
                ref="headerMenu"
                :header-image="headerImage"
                :has-scrolled="contentScrollY > 2"
            >
                <template
                    v-if="moduleTitle"
                    #title
                >
                    {{ moduleTitle }}
                </template>
            </AppHeader>
        </IonHeader>
        <IonContent
            fullscreen
            scroll-events
            data-cy="content-module"
            @ion-scroll="contentScrollY = $event.detail.scrollTop"
        >
            <!-- Position the refresher under the app header in the fixed slot -->
            <IonRefresher
                v-if="supportsRefresher"
                slot="fixed"
                mode="md"
                @ion-refresh="ionRefresh"
            >
                <IonRefresherContent />
            </IonRefresher>

            <!-- Show loading indicator while loading, but only if there's no existing data to prevent jarring loading states on refresh -->
            <div
                v-if="loadingId === id && !data"
                class="py-10 flex items-center justify-center absolute top-0 left-0 right-0 bottom-0 shrink-0"
                data-cy="module-loading"
            >
                <UILoading class="w-12 h-12" />
            </div>

            <div
                v-else-if="error"
                class="p-4 shrink-0"
            >
                <Error
                    :axios-error="error"
                />
            </div>

            <component
                :is="components[component]"
                v-else-if="data && components[component]"
                :config="data"
                class="ion-content-scroll-host"
                :data-cy="`module-component-${data?.component}`"
            />
        </IonContent>
        <IonFooter
            ref="footerMenu"
            class="ion-no-border surface-primary"
        />
    </IonPage>
</template>

<script setup>
    import { provide } from 'vue'
    import { useRouter } from 'vue-router'
    import { omit } from 'lodash'
    import mitt from 'mitt'
    import Error from '@/components/ui/Error.vue'

    import { statusBar } from '@/services'
    import components from '@/constants/components'
    import componentTypes from '@/constants/componentTypes'

    const router = useRouter()

    const moduleStore = useModuleStore()

    const authHelpers = useAuthHelpers()
    const moduleHelpers = useModuleHelpers()

    const props = defineProps({
        id: String,
    })

    const contextualEvents = ref(mitt())
    provide('contextualEvents', contextualEvents)

    const { modules, loadingId } = storeToRefs(moduleStore)

    const component = ref()

    const error = ref(null)
    const headerMenu = useTemplateRef('headerMenu')
    const footerMenu = useTemplateRef('footerMenu')
    const contentScrollY = ref(0)

    const moduleId = computed(() => {
        return props.id
    })

    provide('headerMenu', headerMenu)
    provide('footerMenu', footerMenu)
    provide('moduleId', moduleId)

    const appModule = computed(() => modules.value[props.id])
    const data = computed(() => appModule.value?.data)

    const MODULES_WITHOUT_REFRESH = [componentTypes.Tabs, componentTypes.CustomMap]

    const dataMode = computed(() => {
        switch (data.value?.component) {
            case componentTypes.TourStep:
                return 'brand'
            default:
                return 'base'
        }
    })

    const supportsRefresher = computed(() => {
        return appModule.value?.data?.meta?.refresh_url
            || (appModule.value?.link?.url && !MODULES_WITHOUT_REFRESH.includes(appModule.value?.data?.component))
    })

    const moduleTitle = computed(() => {
        return get(data.value, 'meta.title')
    })

    const headerImage = computed(() => {
        return !!get(data.value, 'meta.header_image')
    })

    const loadModule = async (refresh = false) => {
        if (!refresh && data.value) {
            component.value = data.value.component
            return
        }
        error.value = null
        return moduleStore.loadModule(moduleId.value, refresh)
            .then(() => {
                if (!refresh && isNil(component.value)) {
                    // Don't overwrite component if it already exists
                    component.value = data.value.component
                }
            })
            .catch((e) => {
                if (authHelpers.isAuthenticationException(e)) {
                    authHelpers.handleAuthenticationException(
                        e.response.data,
                        router,
                        (payload) => {
                            const switchedUsers = payload?.userSwitched ?? true

                            if (switchedUsers) {
                                // Go back to the first level module and refresh
                                const moduleId = first(moduleStore.stack)
                                const openModuleCount = moduleStore.stack.length
                                moduleStore.stack = [moduleId]
                                moduleStore.modules = {
                                    [moduleId]: omit(moduleStore.modules[moduleId], 'data'),
                                }
                                moduleHelpers.navigateBackToFirstModule(router, openModuleCount)
                            } else {
                                // Reload the same view
                                const moduleId = last(moduleStore.stack)
                                router.replace({ name: 'view', params: { id: moduleId } })
                            }
                        },
                        () => {
                            router.back()
                        },
                    )

                    // Prevent error from appearing while the login view launches
                    return
                }

                if (moduleHelpers.isActionException(e)) {
                    moduleHelpers.handleActionException(router, e.response.data)
                }

                error.value = e
            })
    }

    const refresh = () => {
        return loadModule(true)
    }

    const ionRefresh = async (e) => {
        return refresh()
            .finally(() => {
                e.target.complete()
            })
    }

    watch(data, (value, oldValue) => {
        // Update component if data changes
        if (get(value, 'component') && value.component !== oldValue?.component) {
            component.value = value.component
        }
    }, { deep: true })

    onMounted(() => {
        loadModule()
        contextualEvents.value.on('refresh', refresh)
    })

    onUnmounted(() => {
        contextualEvents.value.off('refresh', refresh)
    })

    onIonViewWillEnter(() => {
        statusBar.setTheme()
    })
</script>
